import { useState, useMemo, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Badge, Button, Checkbox, Flex, Input, Modal, Tag } from "antd"
import { PhoneOutlined, MailOutlined, UserOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"
import DataTable from "ui/DataTable"
import CustomPagination from "components/common/CustomPagination"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { checkInGuests, checkOutGuests } from "store/reservations"
import BookingTag from "../../BookingTag"
import { OPERATOR_TO_COLLECT_LABELS } from "../../constants"
import SendMessageButton from "../../SendMessageButton"
import CheckInBadge from "../../CheckIn/CheckInBadge/CheckInBadge"
import CheckInGuestsModal from "../../CheckIn/CheckInGuestsModal/CheckInGuestsModal"
import CheckOutGuestsModal from "../../CheckIn/CheckOutGuestsModal/CheckOutGuestsModal"
import IncompleteDocumentsModal from "../../CheckIn/IncompleteDocumentsModal/IncompleteDocumentsModal"
import { useCheckInFlow } from "../../CheckIn/useCheckInFlow"
import { deriveCheckInState } from "../../CheckIn/checkInUtils"
import { COLOR_TO_TAG, PAYMENT_FILTERS, WAIVER_FILTERS, CHECKIN_FILTERS, CHECKIN_STATE_MAP, EMPTY_FILTERS } from "./constants"
import styles from "./index.module.css"

const StatusTag = ({ label, color }) => {
  if (OPERATOR_TO_COLLECT_LABELS.includes(label)) {
    return <BookingTag color="default" className={styles.operatorTag}>{label}</BookingTag>
  }
  return <BookingTag color={COLOR_TO_TAG[color] ?? "default"}>{label}</BookingTag>
}

StatusTag.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired
}

const DocsCell = ({ docsSign, docsTotal }) => (
  <BookingTag color={docsSign === docsTotal ? "success" : "error"}>
    <DSIcon name="file" width={14} height={14} fill="currentColor" />
    {docsSign}/{docsTotal}
  </BookingTag>
)

DocsCell.propTypes = {
  docsSign: PropTypes.number.isRequired,
  docsTotal: PropTypes.number.isRequired
}

// Mobile card row: the In/Out badge is rendered by the parent (shares the check-in flow).
const MobileReservationCard = ({ record, checked, onToggle, badge }) => (
  <div className={styles.mobileCard}>
    <Flex align="flex-start" gap={10}>
      <Flex vertical align="center" gap={12} className={styles.cardRail}>
        <span className={styles.cardBadge}>{badge}</span>
        <Checkbox checked={checked} onChange={onToggle} />
      </Flex>
      <Flex vertical gap={6} className={styles.cardBody}>
        <Flex align="flex-start" justify="space-between" gap={8}>
          <Flex align="center" gap={8} className={styles.cardNameRow}>
            <span className={styles.clientName}>{record.name}</span>
            <Flex align="center" gap={4} className={styles.cardGuests}>
              <UserOutlined className={styles.guestIcon} />
              <span className={styles.guestCount}>{record.guestCount}</span>
            </Flex>
          </Flex>
          <span className={styles.cardBookingId}>{record.bookingId}</span>
        </Flex>
        {(record.paymentTag || record.collectionTag) && (
          <Flex align="center" gap={6} wrap="wrap">
            {record.paymentTag && <StatusTag label={record.paymentTag.label} color={record.paymentTag.color} />}
            {record.collectionTag && <StatusTag label={record.collectionTag.label} color={record.collectionTag.color} />}
          </Flex>
        )}
        <DocsCell docsSign={record.docsSign} docsTotal={record.docsTotal} />
      </Flex>
    </Flex>
  </div>
)

MobileReservationCard.propTypes = {
  record: PropTypes.object.isRequired,
  checked: PropTypes.bool,
  onToggle: PropTypes.func,
  badge: PropTypes.node
}

const FilterChip = ({ label, onRemove }) => (
  <Tag
    className={styles.filterChip}
    closeIcon={<DSIcon name="circledClose" width={14} height={14} fill="var(--color-sportsman-brand)" />}
    onClose={onRemove}
    closable
  >
    {label}
  </Tag>
)

FilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
}

const ToggleChip = ({ label, active, onClick }) => (
  <button
    type="button"
    className={`${styles.toggleChip} ${active ? styles.toggleChipActive : ""}`}
    onClick={onClick}
  >
    {label}
  </button>
)

ToggleChip.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
}

const matchesSearch = (reservation, query) =>
  reservation.name?.toLowerCase().includes(query) ||
  reservation.email?.toLowerCase().includes(query) ||
  reservation.phone?.toLowerCase().includes(query) ||
  reservation.bookingId?.toLowerCase().includes(query)

const matchesPaymentFilter = (reservation, payment) => {
  if (payment.length === 0) return true
  const isPaid = reservation.paymentTag?.color === "green"
  if (payment.includes("Paid") && !payment.includes("Balance Due")) return isPaid
  if (payment.includes("Balance Due") && !payment.includes("Paid")) return !isPaid
  return true
}

const matchesWaiverFilter = (reservation, waiver) => {
  if (waiver.length === 0) return true
  const isSigned = reservation.docsSign === reservation.docsTotal
  if (waiver.includes("Signed") && !waiver.includes("Unsigned")) return isSigned
  if (waiver.includes("Unsigned") && !waiver.includes("Signed")) return !isSigned
  return true
}

const matchesCheckinFilter = (reservation, checkin) => {
  if (checkin.length === 0) return true
  const states = checkin.map((label) => CHECKIN_STATE_MAP[label])
  return states.includes(deriveCheckInState(reservation.checkedInCount, reservation.guestCount, reservation.checkedOutCount))
}

const applyFilters = (reservations, filters) =>
  reservations.filter(
    (reservation) =>
      matchesPaymentFilter(reservation, filters.payment) &&
      matchesWaiverFilter(reservation, filters.waiver) &&
      matchesCheckinFilter(reservation, filters.checkin)
  )

const buildActiveChips = (filters) => {
  const chips = []
  if (filters.waiver.includes("Unsigned")) chips.push({ key: "waiver-unsigned", label: "Missing Waivers/Docs", group: "waiver", value: "Unsigned" })
  if (filters.waiver.includes("Signed")) chips.push({ key: "waiver-signed", label: "Signed Waivers", group: "waiver", value: "Signed" })
  filters.payment.forEach((value) => chips.push({ key: `payment-${value}`, label: value, group: "payment", value }))
  filters.checkin.forEach((value) => chips.push({ key: `checkin-${value}`, label: value, group: "checkin", value }))
  return chips
}

const ReservationsTable = ({ reservations = [], onRefresh }) => {
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [data, setData] = useState(reservations)
  const checkIn = useCheckInFlow(checkInGuests, checkOutGuests, onRefresh)
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)

  // In/Out badge for a reservation — shared by the table column and the mobile cards.
  const renderCheckInBadge = useCallback((record) => {
    const row = {
      id: record.uuid,
      guestTypes: record.guestTypes,
      checkedInCount: record.checkedInCount,
      checkedOutCount: record.checkedOutCount,
      guestCount: record.guestCount
    }
    const guestTypes = checkIn.getGuestTypes(row)
    const checkedInCount = checkIn.getCheckedInCount(row)
    const checkedOutCount = checkIn.getCheckedOutCount(row)
    const state = deriveCheckInState(checkedInCount, record.guestCount, checkedOutCount)
    const target = { id: record.uuid, bookingId: record.uuid, name: record.name, guestTypes, guestCount: record.guestCount }
    return (
      <CheckInBadge
        state={state}
        checkedInCount={checkedInCount}
        checkedOutCount={checkedOutCount}
        totalGuests={record.guestCount}
        onCheckIn={() => checkIn.openCheckIn(target, (record.docsSign ?? 0) >= (record.docsTotal ?? 0))}
        onCheckOut={() => checkIn.openCheckOut(target)}
        onReset={() => checkIn.reset(target)}
      />
    )
  }, [checkIn])

  // Keep the table in sync when the detail data is refetched (e.g. after a mutation)
  useEffect(() => {
    setData(reservations)
  }, [reservations])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [pendingFilters, setPendingFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)

  const toggleGroupValue = (current, value) =>
    current.includes(value) ? current.filter((v) => v !== value) : [...current, value]

  const togglePending = (group, value) => {
    setPendingFilters((prev) => ({ ...prev, [group]: toggleGroupValue(prev[group], value) }))
  }

  const activeFilterCount =
    appliedFilters.payment.length + appliedFilters.waiver.length + appliedFilters.checkin.length

  const activeChips = useMemo(() => buildActiveChips(appliedFilters), [appliedFilters])

  const removeChip = (group, value) => {
    setAppliedFilters((prev) => ({ ...prev, [group]: prev[group].filter((v) => v !== value) }))
    setPage(1)
  }

  const filteredByFilters = useMemo(() => applyFilters(data, appliedFilters), [data, appliedFilters])

  const filtered = useMemo(() => {
    const query = search.toLowerCase()
    if (!query) return filteredByFilters
    return filteredByFilters.filter((reservation) => matchesSearch(reservation, query))
  }, [filteredByFilters, search])

  const pendingFilterCount = pendingFilters.payment.length + pendingFilters.waiver.length + pendingFilters.checkin.length

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filtered.length / pageSize)

  const clearSelection = () => setSelectedIds([])

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const pagedIds = paged.map((reservation) => reservation.id)
  const allPagedSelected = pagedIds.length > 0 && pagedIds.every((id) => selectedIds.includes(id))
  const somePagedSelected = pagedIds.some((id) => selectedIds.includes(id))

  const toggleSelectAll = () =>
    setSelectedIds((prev) =>
      allPagedSelected ? prev.filter((id) => !pagedIds.includes(id)) : Array.from(new Set([...prev, ...pagedIds]))
    )

  const openFilterModal = () => {
    setPendingFilters(appliedFilters)
    setFilterModalOpen(true)
  }

  const applyFilterModal = () => {
    setAppliedFilters(pendingFilters)
    setPage(1)
    setFilterModalOpen(false)
  }

  const clearFilterModal = () => {
    setPendingFilters(EMPTY_FILTERS)
  }

  const columns = useMemo(() => [
    {
      title: "In/Out",
      key: "inOut",
      width: 80,
      render: (_, record) => renderCheckInBadge(record)
    },
    {
      title: "Client",
      key: "client",
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, record) => (
        <Flex vertical gap={2}>
          <span className={styles.clientName}>{record.name}</span>
          <span className={styles.bookingId}>{record.bookingId}</span>
        </Flex>
      )
    },
    {
      title: "Client Contact",
      key: "contact",
      minWidth: 160,
      render: (_, record) => (
        <Flex vertical gap={2}>
          {record.phone && (
            <Flex align="center" gap={6}>
              <PhoneOutlined className={styles.contactIcon} />
              <span className={styles.contactText}>{record.phone}</span>
            </Flex>
          )}
          {record.email && (
            <Flex align="center" gap={6}>
              <MailOutlined className={styles.contactIcon} />
              <span className={styles.contactText}>{record.email}</span>
            </Flex>
          )}
        </Flex>
      )
    },
    {
      title: "Guests",
      key: "guests",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <UserOutlined className={styles.guestIcon} />
          <span className={styles.guestCount}>{record.guestCount}</span>
        </Flex>
      )
    },
    {
      title: "Payment",
      key: "payment",
      width: 150,
      sorter: (a, b) => (a.paymentTag?.label || "").localeCompare(b.paymentTag?.label || ""),
      render: (_, record) => (
        <Flex vertical gap={4}>
          {record.paymentTag && <StatusTag label={record.paymentTag.label} color={record.paymentTag.color} />}
          {record.collectionTag && (
            <StatusTag label={record.collectionTag.label} color={record.collectionTag.color} />
          )}
        </Flex>
      )
    },
    {
      title: "Waivers / Docs",
      key: "docs",
      width: 160,
      sorter: (a, b) => (a.docsSign / (a.docsTotal || 1)) - (b.docsSign / (b.docsTotal || 1)),
      render: (_, record) => <DocsCell docsSign={record.docsSign} docsTotal={record.docsTotal} />
    }
  ], [renderCheckInBadge])

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys)
  }

  const paginationMetadata = {
    page,
    total_pages: totalPages,
    next_page: page < totalPages ? page + 1 : null,
    prev_page: page > 1 ? page - 1 : null
  }

  return (
    <Flex vertical className={styles.container}>
      <Flex gap={10} vertical className={styles.tableHeader}>
        <span className={styles.title}>Reservations</span>
        <Flex align="center" gap={8} className="w-max">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className={styles.searchInput}
            allowClear
          />
          <Badge count={activeFilterCount} size="small">
            <Button
              className={styles.slidersBtn}
              icon={<DSIcon name="filterSliders" width={16} height={16} fill="var(--color-primary)" />}
              onClick={openFilterModal}
            />
          </Badge>
        </Flex>

        {activeChips.length > 0 && (
          <Flex align="center" gap={6} wrap="wrap">
            {activeChips.map((chip) => (
              <FilterChip key={chip.key} label={chip.label} onRemove={() => removeChip(chip.group, chip.value)} />
            ))}
            <span className={styles.resultsCount}>{filtered.length} results</span>
          </Flex>
        )}
      </Flex>

      {selectedIds.length > 0 && (
        <Flex align="center" gap={8} className={styles.selectionBar}>
          <span className={styles.selectionCount}>{selectedIds.length} selected</span>
          <SendMessageButton
            recipients={data.filter((reservation) => selectedIds.includes(reservation.id))}
            label="Message Clients"
            buttonProps={{ type: "primary", size: "small", shape: "round" }}
            setSelectedRowKeys={clearSelection}
          />
          <button type="button" className={styles.clearSelectionBtn} onClick={clearSelection}>
            <CloseOutlined />
          </button>
        </Flex>
      )}

      {isMobile ? (
        <Flex vertical gap={10} className={styles.cardList}>
          <Checkbox
            checked={allPagedSelected}
            indeterminate={somePagedSelected && !allPagedSelected}
            onChange={toggleSelectAll}
            className={styles.selectAll}
          >
            Select all
          </Checkbox>
          {paged.map((record) => (
            <MobileReservationCard
              key={record.id}
              record={record}
              checked={selectedIds.includes(record.id)}
              onToggle={() => toggleSelect(record.id)}
              badge={renderCheckInBadge(record)}
            />
          ))}
        </Flex>
      ) : (
        <div className={styles.tableWrapper}>
          <DataTable
            columns={columns}
            dataSource={paged}
            rowKey="id"
            rowSelection={rowSelection}
            pagination={false}
          />
        </div>
      )}

      <CustomPagination
        metadata={paginationMetadata}
        pageSize={pageSize}
        total={filtered.length}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(1) }}
        pageSizeOptions={[5, 10, 25, 50]}
      />


      <Modal
        title="Filters"
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={
          <Flex justify="space-between" align="center">
            <Button onClick={clearFilterModal}>Clear all</Button>
            <Button type="primary" onClick={applyFilterModal}>
              Show results{pendingFilterCount > 0 ? ` (${pendingFilterCount})` : ""}
            </Button>
          </Flex>
        }
        width={420}
      >
        <Flex vertical gap={20} className={styles.filterModalBody}>
          <Flex vertical gap={8}>
            <span className={styles.filterGroupLabel}>Payment</span>
            <Flex gap={8} wrap="wrap">
              {PAYMENT_FILTERS.map((value) => (
                <ToggleChip
                  key={value}
                  label={value}
                  active={pendingFilters.payment.includes(value)}
                  onClick={() => togglePending("payment", value)}
                />
              ))}
            </Flex>
          </Flex>
          <Flex vertical gap={8}>
            <span className={styles.filterGroupLabel}>Waivers/Documents</span>
            <Flex gap={8} wrap="wrap">
              {WAIVER_FILTERS.map((value) => (
                <ToggleChip
                  key={value}
                  label={value}
                  active={pendingFilters.waiver.includes(value)}
                  onClick={() => togglePending("waiver", value)}
                />
              ))}
            </Flex>
          </Flex>
          <Flex vertical gap={8}>
            <span className={styles.filterGroupLabel}>Check-in</span>
            <Flex gap={8} wrap="wrap">
              {CHECKIN_FILTERS.map((value) => (
                <ToggleChip
                  key={value}
                  label={value}
                  active={pendingFilters.checkin.includes(value)}
                  onClick={() => togglePending("checkin", value)}
                />
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Modal>

      <IncompleteDocumentsModal
        open={!!checkIn.incompleteItem}
        onClose={checkIn.closeIncomplete}
        onOverride={checkIn.handleOverride}
      />

      <CheckInGuestsModal
        open={!!checkIn.checkInItem}
        onClose={checkIn.closeCheckIn}
        bookingId={checkIn.checkInItem?.bookingId}
        clientName={checkIn.checkInItem?.name}
        guestTypes={checkIn.checkInItem?.guestTypes}
        onConfirm={checkIn.handleConfirmCheckIn}
      />

      <CheckOutGuestsModal
        open={!!checkIn.checkOutItem}
        onClose={checkIn.closeCheckOut}
        bookingId={checkIn.checkOutItem?.bookingId}
        clientName={checkIn.checkOutItem?.name}
        guestTypes={checkIn.checkOutItem?.guestTypes}
        onConfirm={checkIn.handleConfirmCheckOut}
      />
    </Flex>
  )
}

ReservationsTable.propTypes = {
  onRefresh: PropTypes.func,
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      uuid: PropTypes.string,
      bookingId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      phone: PropTypes.string,
      email: PropTypes.string,
      guestCount: PropTypes.number,
      checkedInCount: PropTypes.number,
      checkInState: PropTypes.string,
      paymentTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      collectionTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      docsSign: PropTypes.number,
      docsTotal: PropTypes.number
    })
  )
}

export default ReservationsTable
